import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { X } from "lucide-react";
import { sectionsApi } from "../../api";
import type { Section } from "../../types";

const schema = z.object({
  id: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/, "lowercase, digits, dashes")
    .optional(),
  name: z.string().min(1).max(128),
  order: z.coerce.number().int().nonnegative(),
});
type FormValues = z.infer<typeof schema>;

interface Props {
  departmentId: string;
  existing: Section | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function SectionForm({ departmentId, existing, onClose, onSaved }: Props) {
  const isEdit = !!existing;
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: existing
      ? { id: existing.id, name: existing.name, order: existing.order }
      : { name: "", order: 999 },
  });

  async function onSubmit(v: FormValues) {
    try {
      if (isEdit && existing) {
        await sectionsApi.update(existing.id, { name: v.name, order: v.order });
        toast.success("Saved");
      } else {
        await sectionsApi.create({
          id: v.id || undefined,
          department_id: departmentId,
          name: v.name,
          order: v.order,
        });
        toast.success("Section created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error((err as { message?: string }).message ?? "Save failed");
    }
  }

  return (
    <div className="fixed inset-0 z-40 bg-ink-900/40 backdrop-blur-sm grid place-items-center p-4 animate-fade-in">
      <div className="card !rounded-2xl w-full max-w-md shadow-modal animate-scale-in">
        <div className="px-6 py-4 border-b border-ink-100 flex items-center justify-between">
          <h2 className="display text-[18px] font-medium tracking-tight">
            {isEdit ? "Edit section" : "New section"}
          </h2>
          <button onClick={onClose} className="btn-icon" aria-label="close">
            <X className="size-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-5 space-y-4">

          <div>
            <label className="label">Name</label>
            <input className="input" {...register("name")} placeholder="Tools & Apps" autoFocus />
            {errors.name && <p className="text-[11.5px] text-accent-600 mt-1">{errors.name.message}</p>}
          </div>
          <div>
            <label className="label">Order</label>
            <input type="number" className="input" {...register("order")} />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-ink-100 mt-4">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? "SavingÃ¢â‚¬Â¦" : isEdit ? "Save changes" : "Create section"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
